export type FocusSeriesPoint = {
  label: string;
  seconds: number;
  opens: number | null;
};

export function shapeDaySeries(
  hourly: Array<{ hour: number; seconds: number; opens?: number }> | undefined,
): { points: FocusSeriesPoint[]; hasOpens: boolean } {
  const list = hourly ?? [];
  const hasOpens = list.some((bucket) => bucket.opens != null);
  const secondsByHour = new Map<number, number>();
  const opensByHour = new Map<number, number>();
  for (const bucket of list) {
    if (bucket.hour < 0 || bucket.hour > 23) continue;
    secondsByHour.set(bucket.hour, (secondsByHour.get(bucket.hour) ?? 0) + bucket.seconds);
    if (bucket.opens != null) {
      opensByHour.set(bucket.hour, (opensByHour.get(bucket.hour) ?? 0) + bucket.opens);
    }
  }
  const points: FocusSeriesPoint[] = Array.from({ length: 24 }, (_, hour) => ({
    label: String(hour).padStart(2, "0"),
    seconds: secondsByHour.get(hour) ?? 0,
    opens: hasOpens ? (opensByHour.get(hour) ?? 0) : null,
  }));
  return { points, hasOpens };
}

export function shapeWeekSeries(
  days: Array<{ date: string; seconds: number; opens: number | null }>,
): { points: FocusSeriesPoint[]; hasOpens: boolean } {
  const hasOpens = days.some((day) => day.opens != null);
  const points: FocusSeriesPoint[] = days.map((day) => ({
    label: day.date,
    seconds: day.seconds,
    opens: hasOpens ? (day.opens ?? 0) : null,
  }));
  return { points, hasOpens };
}

export function concatAppHourly(
  topApps: Array<{ hourly?: Array<{ hour: number; seconds: number; opens?: number }> }>,
): Array<{ hour: number; seconds: number; opens?: number }> {
  const buckets: Array<{ hour: number; seconds: number; opens?: number }> = [];
  for (const app of topApps) {
    if (app.hourly) buckets.push(...app.hourly);
  }
  return buckets;
}

export function hourlyForAppEntity(
  topApps: Array<{
    appIdentifier?: string;
    displayName: string;
    category?: string;
    durationSeconds: number;
    opens?: number;
    hourly?: Array<{ hour: number; seconds: number; opens?: number }>;
  }>,
  options: { appIdentifier?: string; displayName: string; isCategory?: boolean },
): Array<{ hour: number; seconds: number; opens?: number }> | undefined {
  const { appIdentifier, displayName, isCategory } = options;
  if (isCategory) {
    const buckets: Array<{ hour: number; seconds: number; opens?: number }> = [];
    for (const app of topApps) {
      if (app.category !== displayName) continue;
      if (app.hourly) buckets.push(...app.hourly);
    }
    return buckets.length > 0 ? buckets : undefined;
  }
  const targetKey = appIdentifier ?? displayName;
  const app = topApps.find((row) => (row.appIdentifier ?? row.displayName) === targetKey);
  return app?.hourly;
}

export function aggregateHabitHourlyProfile(
  dailyHourlies: Array<Array<{ hour: number; seconds: number; opens?: number }> | undefined>,
): { points: FocusSeriesPoint[]; hasOpens: boolean; daysWithData: number } {
  if (dailyHourlies.length === 0) {
    return { ...shapeDaySeries(undefined), daysWithData: 0 };
  }
  const shaped = dailyHourlies.map((hourly) => shapeDaySeries(hourly));
  const hasOpens = shaped.some((day) => day.hasOpens);
  const dayCount = shaped.length;
  const points: FocusSeriesPoint[] = Array.from({ length: 24 }, (_, hour) => ({
    label: String(hour).padStart(2, "0"),
    seconds: shaped.reduce((sum, day) => sum + day.points[hour]!.seconds, 0) / dayCount,
    opens: hasOpens
      ? shaped.reduce((sum, day) => sum + (day.points[hour]!.opens ?? 0), 0) / dayCount
      : null,
  }));
  return { points, hasOpens, daysWithData: dayCount };
}
